import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, User, DollarSign, Sliders } from 'lucide-react';
import PageHeader from '../../../components/common/PageHeader';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import {
  Combobox,
  ComboboxInput,
  ComboboxContent,
  ComboboxList,
  ComboboxItem,
  ComboboxEmpty,
} from '../../../components/ui/combobox';
import { validateManagerForm } from '../validation';
import { mamManagerService } from '../services/mamService';
import { LEVERAGE_OPTIONS, TRADING_SYMBOLS } from '../constants';
import { MAM_ROUTES } from '../../../constants/routeConstants';
import type { RiskLevel } from '../types';
import {
  preventInvalidNumberInput,
  preventInvalidNumberPaste,
  preventInvalidNameInput,
} from '../../../utils/validationMessages';

export default function MAMCreateManager() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState<{
    name: string;
    email: string;
    accountNumber: string;
    performanceFee: number;
    managementFee: number;
    leverage: string;
    riskLevel: RiskLevel;
    tradingSymbols: string[];
  }>({
    name: '',
    email: '',
    accountNumber: '',
    performanceFee: 20,
    managementFee: 2.0,
    leverage: '1:200',
    riskLevel: 'medium',
    tradingSymbols: ['EURUSD', 'GBPUSD'],
  });

  const handleSymbolToggle = (sym: string) => {
    setFormData(prev => {
      const exists = prev.tradingSymbols.includes(sym);
      if (exists) {
        return {
          ...prev,
          tradingSymbols: prev.tradingSymbols.filter(s => s !== sym),
        };
      }
      return { ...prev, tradingSymbols: [...prev.tradingSymbols, sym] };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validateManagerForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setSubmitting(true);
      await mamManagerService.create(formData);
      navigate(MAM_ROUTES.MAM_MANAGERS);
    } catch {
      setErrors({ form: 'Failed to create MAM manager. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 pb-6 mt-2 w-full">
      <PageHeader
        title="Create MAM Manager"
        description="Register a new master trading account into the MAM allocation engine"
      />

      <form onSubmit={handleSubmit} className="space-y-6">
        {errors.form && (
          <div className="p-3 bg-rose-50 text-rose-700 border border-rose-200 rounded-lg text-xs">
            {errors.form}
          </div>
        )}

        {/* Step 1: Basic Information */}
        <Card className="shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <User className="w-4 h-4 text-indigo-600" />
              Master Trader Information
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
            <div>
              <label className="block font-medium text-foreground mb-1">
                Manager Full Name / Firm
              </label>
              <Input
                value={formData.name}
                onKeyDown={preventInvalidNameInput}
                onChange={e => {
                  setFormData({ ...formData, name: e.target.value });
                  if (errors.name)
                    setErrors(prev => ({ ...prev, name: undefined as any }));
                }}
                placeholder="e.g. Apex Trading Ltd"
                className={`h-8 text-xs ${errors.name ? 'border-red-500' : ''}`}
              />
              {errors.name && (
                <p className="text-rose-500 text-[10px] mt-1">{errors.name}</p>
              )}
            </div>

            <div>
              <label className="block font-medium text-foreground mb-1">
                Email Address
              </label>
              <Input
                type="email"
                value={formData.email}
                onChange={e => {
                  setFormData({ ...formData, email: e.target.value });
                  if (errors.email)
                    setErrors(prev => ({ ...prev, email: undefined as any }));
                }}
                placeholder="manager@domain.com"
                className={`h-8 text-xs ${errors.email ? 'border-red-500' : ''}`}
              />
              {errors.email && (
                <p className="text-rose-500 text-[10px] mt-1">{errors.email}</p>
              )}
            </div>

            <div>
              <label className="block font-medium text-foreground mb-1">
                Master MT4/MT5 Account #
              </label>
              <Input
                value={formData.accountNumber}
                onChange={e =>
                  setFormData({ ...formData, accountNumber: e.target.value })
                }
                placeholder="MT5-998800"
                className="h-8 text-xs font-mono"
              />
              {errors.accountNumber && (
                <p className="text-rose-500 text-[10px] mt-1">
                  {errors.accountNumber}
                </p>
              )}
            </div>

            <div>
              <label className="block font-medium text-foreground mb-1">
                Account Leverage
              </label>
              <Combobox
                items={LEVERAGE_OPTIONS}
                value={formData.leverage}
                onValueChange={val =>
                  setFormData({ ...formData, leverage: val })
                }
              >
                <ComboboxInput
                  className="h-8 text-xs font-mono"
                  placeholder="Select leverage..."
                />
                <ComboboxContent>
                  <ComboboxEmpty>No options found</ComboboxEmpty>
                  <ComboboxList
                    renderItem={item => (
                      <ComboboxItem key={item} value={item}>
                        {item}
                      </ComboboxItem>
                    )}
                  />
                </ComboboxContent>
              </Combobox>
            </div>
          </CardContent>
        </Card>

        {/* Step 2: Fees & Risk Level */}
        <Card className="shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-emerald-600" />
              Fee Schedule & Risk Limits
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
            <div>
              <label className="block font-medium text-foreground mb-1">
                Performance Fee (%)
              </label>
              <Input
                type="number"
                value={formData.performanceFee}
                onKeyDown={preventInvalidNumberInput}
                onPaste={preventInvalidNumberPaste}
                onChange={e =>
                  setFormData({
                    ...formData,
                    performanceFee: parseFloat(e.target.value) || 0,
                  })
                }
                className="h-8 text-xs"
              />
              {errors.performanceFee && (
                <p className="text-rose-500 text-[10px] mt-1">
                  {errors.performanceFee}
                </p>
              )}
            </div>

            <div>
              <label className="block font-medium text-foreground mb-1">
                Management Fee (% p.a.)
              </label>
              <Input
                type="number"
                step="0.1"
                value={formData.managementFee}
                onKeyDown={preventInvalidNumberInput}
                onPaste={preventInvalidNumberPaste}
                onChange={e =>
                  setFormData({
                    ...formData,
                    managementFee: parseFloat(e.target.value) || 0,
                  })
                }
                className="h-8 text-xs"
              />
              {errors.managementFee && (
                <p className="text-rose-500 text-[10px] mt-1">
                  {errors.managementFee}
                </p>
              )}
            </div>

            <div>
              <label className="block font-medium text-foreground mb-1">
                Strategy Risk Profile
              </label>
              <Combobox
                items={['Low Risk', 'Medium Risk', 'High Risk']}
                value={
                  formData.riskLevel === 'low'
                    ? 'Low Risk'
                    : formData.riskLevel === 'medium'
                      ? 'Medium Risk'
                      : 'High Risk'
                }
                onValueChange={val => {
                  const map: Record<string, RiskLevel> = {
                    'Low Risk': 'low',
                    'Medium Risk': 'medium',
                    'High Risk': 'high',
                  };
                  setFormData({ ...formData, riskLevel: map[val] ?? 'medium' });
                }}
              >
                <ComboboxInput
                  className="h-8 text-xs"
                  placeholder="Select risk level..."
                />
                <ComboboxContent>
                  <ComboboxEmpty>No options found</ComboboxEmpty>
                  <ComboboxList
                    renderItem={item => (
                      <ComboboxItem key={item} value={item}>
                        {item}
                      </ComboboxItem>
                    )}
                  />
                </ComboboxContent>
              </Combobox>
            </div>
          </CardContent>
        </Card>

        {/* Step 3: Trading Symbols */}
        <Card className="shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Sliders className="w-4 h-4 text-purple-600" />
              Approved Symbols
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Select financial instruments enabled for trade allocation.
            </p>
            <div className="flex flex-wrap gap-2">
              {TRADING_SYMBOLS.map(sym => {
                const selected = formData.tradingSymbols.includes(sym);
                return (
                  <button
                    key={sym}
                    type="button"
                    onClick={() => handleSymbolToggle(sym)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all flex items-center gap-1.5 ${
                      selected
                        ? 'border-indigo-600 bg-indigo-50 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300'
                        : 'border-border text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    {selected && <Check className="w-3.5 h-3.5" />}
                    {sym}
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-3 pt-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => navigate(MAM_ROUTES.MAM_MANAGERS)}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            size="sm"
            disabled={submitting}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            {submitting ? 'Creating Manager...' : 'Submit & Register Manager'}
          </Button>
        </div>
      </form>
    </div>
  );
}
